// Dashed drop targets for Inbox triage (duration / project / context).
import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import InboxDropzone from '@/features/organize/components/InboxDropzone';
import { useInboxDropzoneGridRoving } from '@/features/organize/hooks/useInboxDropzoneGridRoving';
import { useT } from '@/i18n/useT';

export default function InboxDropzones({
  zones,
  emptyMessage,
  addPlaceholder,
  onAddTag,
  addInputTestId = 'inbox-tag-add-input',
  highlightZoneId,
  showDropHint = false,
  selectedTaskId = null,
  onAssignToZone,
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [assignAnnouncement, setAssignAnnouncement] = useState('');
  const assignLockRef = useRef(false);

  const handleAddKeyDown = (event) => {
    if (event.key !== 'Enter' || !onAddTag) return;
    event.preventDefault();
    const result = onAddTag(draft);
    if (result === 'added') setDraft('');
  };

  const zoneList = useMemo(() => zones ?? [], [zones]);
  const zoneCount = zoneList.length;

  const handleActivate = useCallback(
    (index) => {
      if (!selectedTaskId || !onAssignToZone || assignLockRef.current) return;
      const zone = zoneList[index];
      if (!zone) return;
      assignLockRef.current = true;
      onAssignToZone(zone.id, zone.label);
      setAssignAnnouncement(
        t('inboxdropzone.assignedAnnouncement', 'Task assigned to {label}', {
          label: zone.label,
        }),
      );
      window.requestAnimationFrame(() => {
        assignLockRef.current = false;
      });
    },
    [onAssignToZone, selectedTaskId, t, zoneList],
  );

  const { activeIndex, setActiveIndex, onKeyDown, getTabIndex } =
    useInboxDropzoneGridRoving(zoneCount, handleActivate);

  const showTagAdd = Boolean(onAddTag);
  const hasZones = zoneCount > 0;

  if (!hasZones && !showTagAdd) {
    return (
      <Box data-testid="inbox-dropzones" sx={{ p: 3, color: 'text.subtle' }}>
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  const toolbarLabel = t(
    'inboxdropzones.toolbarLabel',
    'Assign selected task to a drop zone',
  );

  return (
    <Box
      data-testid="inbox-dropzones"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: '100%',
        p: 1,
        boxSizing: 'border-box',
      }}
    >
      <Box
        data-testid="inbox-dropzone-assign-live"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {assignAnnouncement}
      </Box>
      {showTagAdd ? (
        <TextField
          size="small"
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleAddKeyDown}
          placeholder={addPlaceholder}
          inputProps={{
            'data-testid': addInputTestId,
            'aria-label': addPlaceholder,
          }}
        />
      ) : null}
      {!hasZones && emptyMessage ? (
        <Typography variant="body2" sx={{ color: 'text.subtle', px: 1 }}>
          {emptyMessage}
        </Typography>
      ) : !hasZones ? null : (
        <Box
          role="toolbar"
          aria-label={toolbarLabel}
          onKeyDown={onKeyDown}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            flex: 1,
          }}
        >
          {zoneList.map((zone, index) => (
            <InboxDropzone
              key={zone.id}
              id={zone.id}
              label={zone.label}
              highlighted={highlightZoneId === zone.id}
              showDropHint={showDropHint}
              tabIndex={getTabIndex(index)}
              isKeyboardFocused={index === activeIndex}
              onRovingFocus={() => setActiveIndex(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
